import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { TipoDeTaxaDeUrgencia } from "@bookdental/schemas";
import { Input, Label } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { useTaxaDeUrgencia } from "@/lib/taxa-de-urgencia";
import { parsePriceToCents } from "@/components/prosthesis/prosthesis-service-draft";

const OPCOES: { valor: TipoDeTaxaDeUrgencia | ""; rotulo: string }[] = [
  { valor: "", rotulo: "Sem taxa" },
  { valor: "FIXA", rotulo: "Valor fixo" },
  { valor: "PERCENTUAL", rotulo: "Porcentagem" },
];

/**
 * Taxa de urgência: configurada uma vez, oferecida (já marcada) no aceite do pedido urgente e
 * lançada como acréscimo. A clínica continua vendo só o aviso de sempre ao pedir.
 */
export function TaxaDeUrgenciaSection() {
  const utils = trpc.useUtils();
  const salva = useTaxaDeUrgencia();
  const [tipo, setTipo] = useState<TipoDeTaxaDeUrgencia | "">("");
  const [valor, setValor] = useState("");
  const chave = JSON.stringify(salva);
  useEffect(() => {
    setTipo(salva?.tipo ?? "");
    setValor(!salva ? "" : salva.tipo === "FIXA" ? (salva.valor / 100).toFixed(2).replace(".", ",") : String(salva.valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const salvar = trpc.organization.setTaxaDeUrgencia.useMutation({
    onSuccess: () => {
      utils.organization.getProfiles.invalidate();
      toast.success("Taxa de urgência atualizada.");
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar a taxa."),
  });

  const numero = tipo === "FIXA" ? parsePriceToCents(valor) : tipo === "PERCENTUAL" ? Number(valor) : null;
  const valido = tipo === "" || (numero != null && Number.isInteger(numero) && numero >= 1 && (tipo !== "PERCENTUAL" || numero <= 100));
  const novo = tipo === "" ? null : { tipo, valor: numero as number };
  const mudou = JSON.stringify(novo) !== chave;

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>Taxa de urgência</Label>
        <div className="flex flex-wrap items-center gap-2">
          <div role="radiogroup" aria-label="Tipo da taxa de urgência" className="inline-flex rounded-md border border-zinc-200 p-0.5 dark:border-border">
            {OPCOES.map((o) => (
              <button
                key={o.rotulo}
                type="button"
                role="radio"
                aria-checked={tipo === o.valor}
                onClick={() => {
                  setTipo(o.valor);
                  setValor("");
                }}
                className={cn(
                  "rounded px-3 py-1.5 text-[12.5px] font-medium transition-colors",
                  tipo === o.valor ? "bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary" : "text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground dark:hover:bg-muted",
                )}
              >
                {o.rotulo}
              </button>
            ))}
          </div>
          {tipo !== "" && (
            <span className="flex items-center gap-1.5 text-sm text-zinc-700 dark:text-foreground/90">
              {tipo === "FIXA" && "R$"}
              <Input
                aria-label={tipo === "FIXA" ? "Valor da taxa" : "Porcentagem da taxa"}
                inputMode="decimal"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder={tipo === "FIXA" ? "50,00" : "20"}
                className="w-24"
              />
              {tipo === "PERCENTUAL" && "% dos serviços"}
            </span>
          )}
        </div>
        <p className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">
          Ao aceitar um pedido urgente (prazo mais curto que o seu), a taxa vem marcada para cobrar; desmarque para dispensar. Ela entra
          como acréscimo do pedido, e a clínica é avisada. A porcentagem é sobre os serviços com preço.
        </p>
      </div>
      <button
        type="button"
        onClick={() => salvar.mutate(novo ?? { tipo: null, valor: null })}
        disabled={salvar.isPending || !valido || !mudou}
        className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
      >
        {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        Salvar taxa
      </button>
    </div>
  );
}
